import { HttpErrorResponse } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import { GbPaginatedResponse } from '@galabid/gb-dashboard/core/data-access'
import { ComponentStore } from '@ngrx/component-store'
import { tapResponse } from '@ngrx/operators'
import { EnvironmentModel } from 'apps/gb-dashboard/src/environments/environment.model'
import { ProfilesEntity } from 'libs/gb-dashboard/user/data-access/src/lib/+state/profiles.models'
import { UsersApiService } from 'libs/gb-dashboard/user/data-access/src/lib/+state/users-api.service'
import { APP_CONFIG } from 'libs/shared/app-config/src/lib/gb-app-config'
import { formatDisplayName } from 'libs/shared/utils/helpers/src/lib/format-display-name'
import { debounceTime, filter, switchMap, tap, withLatestFrom } from 'rxjs/operators'
import { ProfileFilters } from 'libs/gb-dashboard/user/data-access/src/lib/+state/profiles.models'
import { SelectedAuctionFacade } from 'libs/gb-dashboard/auctions/data-access-selected-auction/src/lib/+state/selected-auction.facade'

interface State {
  results: ProfilesEntity[]
  loading: boolean
  offset: number
  count: number
  filters: ProfileFilters
  error?: HttpErrorResponse
}

@Injectable()
export class GbProfileSelectStore extends ComponentStore<State> {
  readonly loading$ = this.select((state) => state.loading)
  readonly count$ = this.select((state) => state.count)
  readonly profiles$ = this.select((state) =>
    state.results.map((profile) => ({
      ...profile,
      full_name: formatDisplayName(profile),
      email: profile.email || profile.invite_email,
      phone_number: profile.phone_number || profile.invite_phone_number,
    })),
  )

  constructor(
    private selectedAuctionFacade: SelectedAuctionFacade,
    private usersApiService: UsersApiService,
    @Inject(APP_CONFIG) private appConfig: EnvironmentModel,
  ) {
    super({ results: [], loading: false, offset: 0, count: 0, filters: {} })
  }

  readonly loadFirstPageProfiles = this.effect<ProfileFilters | void>((trigger$) =>
    trigger$.pipe(
      tap((filters) => {
        this.patchState({ filters: filters ?? {} })
        this.getProfiles({ offset: 0 })
      }),
    ),
  )

  readonly loadNextPageProfiles = this.effect<void>((trigger$) =>
    trigger$.pipe(
      withLatestFrom(this.state$),
      filter(([, state]) => state.results.length < state.count),
      tap(([, state]) => {
        const offset = state.offset + this.appConfig.itemsPageSize
        this.getProfiles({ offset })
      }),
    ),
  )

  readonly searchProfiles = this.effect<string>((search$) =>
    search$.pipe(
      tap(() => this.patchState({ loading: true })),
      debounceTime(500),
      tap((search) => {
        this.patchState((state) => ({ filters: { ...state.filters, search } }))
        this.getProfiles({ offset: 0 })
      }),
    ),
  )

  private getProfiles = this.effect<{ offset: number }>((params$) =>
    params$.pipe(
      withLatestFrom(this.selectedAuctionFacade.urlName$, this.state$),
      switchMap(([{ offset }, urlName, state]) => {
        const filters = { ordering: 'invite_first_name', is_active: true, ...state.filters }
        this.patchState({ loading: true, offset })

        return this.usersApiService.getProfiles(urlName, offset, filters).pipe(
          tapResponse({
            next: (response: GbPaginatedResponse<ProfilesEntity>) =>
              this.patchState((state) => ({
                results: offset === 0 ? response.results : [...state.results, ...response.results],
                count: response.count,
                loading: false,
                offset,
              })),
            error: (error: HttpErrorResponse) => this.patchState({ error, loading: false, offset }),
          }),
        )
      }),
    ),
  )
}
